import React, {
  memo,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import {
  AppContext,
} from '../../../../../../../contexts';

import {
  FlexContainer,
  Select,
} from '../../../../../..';

import AddRemoveButton from '../../AddRemoveButton';

import { SeletorDeCorEstilizado } from './styles';

/**
 * @typedef {object} RegraGradientePorFiltroProps
 * @prop {SetState<RegraGradientePorFiltro>} alterarGradientePorFiltro
 * Callback para alterar as regras
 * @prop {RegraGradientePorFiltro[]} gradientePorFiltro Lista de regras definidas atualmente
 * @prop {RegraGradientePorFiltro} regra Regra para esta componente
 * @prop {number} posicaoRegra Indíce da regra desta componente
 * @prop {boolean} permitirAplicar Booleano que define se a regra é aplicavel
 * @prop {SetState<boolean>} setPermitirAplicar setState de validação da regra
 */
/**
 * @param {RegraGradientePorFiltroProps} props
 * @returns {JSX.Element} Componente que define uma regra de escala de cores
 */
function RegraGradientePorFiltro(props) {
  const {
    gradientePorFiltro,
    permitirAplicar,
    regra,
    posicaoRegra,
    alterarGradientePorFiltro,
    setPermitirAplicar,
  } = props;

  const { activeFilters } = useContext(AppContext);

  const { id, cor, item, intervalo, desabilitar } = regra;

  const adicionarRegra = useCallback(() => {
    alterarGradientePorFiltro(atual => {
      const { length } = atual;
      const item = {
        id: atual[length - 1].id + 1,
        cor: '#0B8209',
        item: '',
      };
      return [...atual, item];
    });
  }, [alterarGradientePorFiltro]);

  const removerRegra = useCallback(() => {
    alterarGradientePorFiltro(old => old.filter(cs => cs.id !== id));
  }, [alterarGradientePorFiltro, id]);

  const alterarRegra = useCallback((valor, chave) => {
    alterarGradientePorFiltro(old => old.map((cs) => {
      if (cs.id === id) {
        cs[chave] = valor;
      }
      return cs;
    }));
  }, [alterarGradientePorFiltro, id]);

  /**
   * Opções de filtro para regra de cor
   * @type {SelectComponentOption[]}
   */
  const opcoesSelecao = useMemo(() => (
    activeFilters.length > 0
      ? activeFilters.map((item) => ({
        id: item.settings.title.join(', '),
        label: item.settings.title.join(', '),
      }))
      : []
  ), [activeFilters]);

  useEffect(() => {
    alterarRegra(
      item !== '' && !activeFilters.some(({ settings }) => item === settings.title.join(', ')),
      'desabilitar',
    );
  }, [item, activeFilters, alterarRegra]);

  useEffect(() => {
    setPermitirAplicar(
      item !== '' && !gradientePorFiltro.some(regra => regra.id !== id && regra.item === item)
    );
  }, [gradientePorFiltro, id, item, intervalo, setPermitirAplicar]);

  return (
    <FlexContainer
      key={`regra-${id}-gradiente-por-filtro`}
      align="center"
      style={{ margin: '4px 0' }}
    >
      <SeletorDeCorEstilizado
        color={cor}
        setColor={(cor) => alterarRegra(cor, 'cor')}
        tamanho="1.25rem"
        popperTransform="translate(0.5rem, -50%)"
      />

      <FlexContainer
        flow="column"
        style={{ margin: '0 5px' }}
      >
        <Select
          disabled={desabilitar}
          onChange={({ target }) => alterarRegra(target.value, 'item')}
          label="Opções"
          value={item}
          style={{ margin: '0 3px', width: '100%' }}
          formControlStyle={{ minWidth: 210, width: '100%' }}
        >
          {
            desabilitar
              ? [{ id: item, label: item }]
              : opcoesSelecao.sort((a, b) => ((a.label > b.label) ? 1 : -1))
          }
        </Select>
      </FlexContainer>
      <FlexContainer>
        {/* Remove */}
        {
          gradientePorFiltro.length > 1 && (
            <AddRemoveButton
              type="Remover"
              onClick={removerRegra}
              style={{ marginRight: 2 }}
            />
          )
        }

        {/* Add */}
        {
          posicaoRegra === gradientePorFiltro.length - 1 && (
            <AddRemoveButton
              type="Adder"
              onClick={adicionarRegra}
              disabled={!permitirAplicar}
              style={{ marginLeft: 2 }}
            />
          )
        }
      </FlexContainer>
    </FlexContainer>
  );
}

/**
 * Componente de definição da regra de coloração de Escala por Filtro.
 * Define uma cor para um filtro e pode definir um intervalo de quantidade a este filtro
 * @param alterarGradientePorFiltro Callback para alterar as regras
 * @param gradientePorFiltro Lista de regras definidas atualmente
 * @param regra Regra para esta componente
 * @param posicaoRegra Indíce da regra desta componente
 * @param permitirAplicar Booleano que define se a regra é aplicavel
 * @param setPermitirAplicar setState de validação da regra
 */
export default memo(RegraGradientePorFiltro);
